import set from 'platform/utilities/data/set';
import omit from 'platform/utilities/data/omit';

import { sippableId, sippableIdForCondition } from './index';
import { upgradeLegacyKeys } from './reconcile-disability-map';
import { SIDE_OF_BODY_VALUES } from '../constants';

const TE_CONDITIONS_PATH = 'toxicExposure.conditions';
const PRESERVED_KEYS = ['none'];

const getConditions = formData => formData?.toxicExposure?.conditions;

// The Toxic Exposure page keys its checkboxes the same way, so the valid set
// stays in lockstep with the options the Veteran actually sees.
const validSippableIdSet = (disabilities = []) =>
  new Set(disabilities.map(sippableIdForCondition));

const removeOrphanKeys = (conditions, validIds) => {
  const orphanKeys = Object.keys(conditions).filter(
    key => !validIds.has(key) && !PRESERVED_KEYS.includes(key),
  );
  if (orphanKeys.length === 0) return conditions;
  return omit(orphanKeys, conditions);
};

// Saved answers can be keyed on the condition name alone. Runs before any
// sweep, which would otherwise drop them as orphans.
export const upgradeLegacyTEConditionKeys = formData => {
  const conditions = getConditions(formData);
  if (!conditions) return formData;

  const disabilities = formData?.newDisabilities;
  if (!Array.isArray(disabilities)) return formData;

  const next = upgradeLegacyKeys(conditions, disabilities, PRESERVED_KEYS);
  if (next === conditions) return formData;

  return set(TE_CONDITIONS_PATH, next, formData);
};

// Used by the SiP migration, where there is no prior form state to diff
// against — we can only drop keys that no longer correspond to any current
// newDisabilities entry. Bails out when newDisabilities is missing (e.g.
// prefill data before the Veteran has added conditions) to avoid sweeping
// TE entries that are valid but whose conditions haven't been populated yet.
export const sweepOrphanedTEConditions = formData => {
  const upgraded = upgradeLegacyTEConditionKeys(formData);

  const conditions = getConditions(upgraded);
  if (!conditions) return upgraded;

  const disabilities = upgraded?.newDisabilities;
  if (!Array.isArray(disabilities)) return upgraded;

  const validIds = validSippableIdSet(disabilities);
  const next = removeOrphanKeys(conditions, validIds);
  if (next === conditions) return upgraded;

  return set(TE_CONDITIONS_PATH, next, upgraded);
};

// Every key this row could previously have been stored under.
const previousKeysFor = row => {
  const condition =
    typeof row?.condition === 'string' ? row.condition.trim() : '';
  if (!condition) return new Set();

  return new Set([
    sippableId(condition),
    ...SIDE_OF_BODY_VALUES.map(side =>
      sippableId(`${condition}, ${side.toLowerCase()}`),
    ),
  ]);
};

// A side of body change re-keys the row, so the answer has to move with it or
// the sweep below deletes it. Only safe when exactly one key is orphaned: that
// orphan is this row's previous key.
export const migrateTEConditionForRow = (formData, index) => {
  const conditions = getConditions(formData);
  if (!conditions) return formData;

  const disabilities = formData?.newDisabilities;
  if (!Array.isArray(disabilities)) return formData;

  const row = disabilities[index];
  if (!row) return formData;

  const newId = sippableIdForCondition(row);
  if (conditions[newId] !== undefined) return formData;

  const validIds = validSippableIdSet(disabilities);
  const orphans = Object.keys(conditions).filter(
    key => !validIds.has(key) && !PRESERVED_KEYS.includes(key),
  );
  if (orphans.length !== 1) return formData;

  const [oldId] = orphans;
  // Only the side changed, so the orphan has to be this same condition under
  // one of the other sides. Anything else belongs to a different row.
  if (!previousKeysFor(row).has(oldId)) return formData;
  const next = { ...conditions };
  next[newId] = conditions[oldId];
  delete next[oldId];

  return set(TE_CONDITIONS_PATH, next, formData);
};

// Used at edit time from the V2 conditions summary page. Handles both
// rename (migrates the old sippableId value onto the new one) and delete
// (drops orphaned keys) so the Toxic Exposure conditions page never sees
// stale checkbox state. Renames are collected first and applied atomically
// so a slot swap cannot clobber itself.
export const reconcileToxicExposureConditions = (oldFormData, formDataIn) => {
  const newFormData = upgradeLegacyTEConditionKeys(formDataIn);

  const conditions = getConditions(newFormData);
  if (!conditions) return newFormData;

  const oldDisabilities = oldFormData?.newDisabilities || [];
  const newDisabilities = newFormData?.newDisabilities || [];

  let next = { ...conditions };
  let changed = false;

  if (oldDisabilities.length === newDisabilities.length) {
    const renames = [];
    for (let i = 0; i < newDisabilities.length; i += 1) {
      const oldId = sippableIdForCondition(oldDisabilities[i]);
      const newId = sippableIdForCondition(newDisabilities[i]);
      if (oldId !== newId && conditions[oldId] !== undefined) {
        renames.push({ oldId, newId, value: conditions[oldId] });
      }
    }
    if (renames.length > 0) {
      renames.forEach(({ oldId }) => {
        delete next[oldId];
      });
      renames.forEach(({ newId, value }) => {
        next[newId] = value;
      });
      changed = true;
    }
  }

  const validIds = validSippableIdSet(newDisabilities);
  const swept = removeOrphanKeys(next, validIds);
  if (swept !== next) {
    next = swept;
    changed = true;
  }

  if (!changed) return newFormData;
  return set(TE_CONDITIONS_PATH, next, newFormData);
};
